import { useCallback, useEffect, useState } from 'react';
import { PlatformApiError, type Idea, type InterestGroup } from '@sfc/sdk';
import { platform } from '../api/client';
import { compensationLabel, postedLabel, stageLabel } from '../vocabulary';
import { IdeaForm } from './IdeaForm';

/** The API's own limit, said here so the button can explain itself. */
const MAX_LIVE = 5;

type State =
  | { status: 'loading' }
  | { status: 'unreachable' }
  | { status: 'ready'; ideas: Idea[]; waiting: Map<string, number> };

/**
 * What somebody has posted, and the way to post another.
 *
 * Withdrawing is not deleting, and the list says so: a withdrawn idea is still
 * here, out of every deck, with the matches it already made intact. An idea
 * staff have taken down says that too, in its own words, because being told no
 * and changing your mind are not the same thing and an author who is shown the
 * wrong one will post it again.
 */
export function IdeasScreen({
  onChanged,
  onSeeInterest,
}: {
  onChanged: () => void;
  onSeeInterest: () => void;
}): React.JSX.Element {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [editing, setEditing] = useState<Idea | 'new'>();
  /** Withdrawing leaves every deck at once, so it asks first. */
  const [confirming, setConfirming] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      /*
       * How many people are waiting, next to the idea they are waiting on.
       *
       * It lives on another tab, and an idea holder who has to go looking for
       * it mostly does not: the single most motivating fact about something
       * you posted is that somebody answered, and it belongs where you see
       * what you posted.
       */
      const [ideas, inbox] = await Promise.all([
        platform.match.ideas(),
        platform.match.interest().catch(() => ({ ideas: [] as InterestGroup[], waiting: 0 })),
      ]);
      const waiting = new Map(inbox.ideas.map((group) => [group.ideaId, group.pending.length]));
      setState({ status: 'ready', ideas, waiting });
    } catch {
      setState({ status: 'unreachable' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function withdraw(idea: Idea): Promise<void> {
    setBusy(true);
    setProblem(undefined);
    try {
      await platform.match.withdrawIdea(idea.id);
      await load();
      // One fewer idea in other people's decks, and its waiting interest is
      // still answerable, so the other tabs are stale.
      onChanged();
    } catch (error) {
      setProblem(
        error instanceof PlatformApiError ? error.message : 'That did not go through. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <IdeaForm
        {...(editing === 'new' ? {} : { existing: editing })}
        onCancel={() => setEditing(undefined)}
        onDone={() => {
          setEditing(undefined);
          void load();
          onChanged();
        }}
      />
    );
  }

  if (state.status === 'loading') {
    return (
      <p className="loading" data-testid="ideas-loading">
        One moment…
      </p>
    );
  }

  if (state.status === 'unreachable') {
    return (
      <div className="placeholder">
        <h2>We cannot reach the server</h2>
        <p className="notice notice--problem" role="status">
          Nothing you posted has been lost. Try again in a moment.
        </p>
      </div>
    );
  }

  const live = state.ideas.filter((idea) => idea.status === 'live');
  const full = live.length >= MAX_LIVE;

  return (
    <section className="column" aria-label="Your ideas">
      <header className="column__head">
        <h2>Your ideas</h2>
        <p className="fine">
          {live.length} of {MAX_LIVE} looking for people.
          {full ? ' Withdraw one to post another.' : ''}
        </p>
      </header>

      {problem ? (
        <p className="notice notice--problem" role="status" data-testid="ideas-problem">
          {problem}
        </p>
      ) : null}

      {state.ideas.length > 0 && (
        <button
          className="button"
          type="button"
          disabled={full}
          data-testid="post-idea"
          onClick={() => setEditing('new')}
        >
          {full ? `${MAX_LIVE} is the limit` : 'Post another'}
        </button>
      )}

      {state.ideas.length === 0 ? (
        <div className="placeholder" data-testid="ideas-empty">
          <h2>Nothing posted yet</h2>
          <p>
            Describe what you are building and who you are missing. It goes into the decks of people
            who do what you need, without your name on it.
          </p>
        </div>
      ) : (
        <ul className="people">
          {state.ideas.map((idea) => (
            <li key={idea.id}>
              <article className="person" data-testid="own-idea">
                <header className="person__head">
                  <h3 className="person__name">{idea.title}</h3>
                  <p className="fine">
                    {stageLabel(idea.stage)} · {compensationLabel(idea.compensationType)} ·{' '}
                    {postedLabel(idea.createdAt).toLowerCase()}
                  </p>
                </header>

                <p className="person__headline">{idea.pitch}</p>

                <h4 className="card__label">Looking for</h4>
                <ul className="skills">
                  {idea.skills.map((skill) => (
                    <li key={skill.id} className="chip chip--skill">
                      {skill.label}
                    </li>
                  ))}
                </ul>

                {(state.waiting.get(idea.id) ?? 0) > 0 && (
                  <button
                    className="idea__waiting"
                    type="button"
                    data-testid="idea-waiting"
                    onClick={onSeeInterest}
                  >
                    {state.waiting.get(idea.id) === 1
                      ? '1 person is waiting on this'
                      : `${state.waiting.get(idea.id)} people are waiting on this`}
                  </button>
                )}

                <Status idea={idea} />

                {idea.status === 'live' && confirming === idea.id ? (
                  <div className="idea__confirm">
                    <p className="fine">
                      It leaves every deck immediately. Anybody you have already matched with stays.
                    </p>
                    <div className="choices">
                      <button
                        className="button"
                        type="button"
                        disabled={busy}
                        onClick={() => setConfirming(undefined)}
                      >
                        Keep it up
                      </button>
                      <button
                        className="button button--danger"
                        type="button"
                        disabled={busy}
                        data-testid="withdraw-confirm"
                        onClick={() => {
                          setConfirming(undefined);
                          void withdraw(idea);
                        }}
                      >
                        Withdraw it
                      </button>
                    </div>
                  </div>
                ) : idea.status === 'live' ? (
                  <div className="choices">
                    <button
                      className="button"
                      type="button"
                      disabled={busy}
                      data-testid="edit-idea"
                      onClick={() => setEditing(idea)}
                    >
                      Edit
                    </button>
                    <button
                      className="button button--danger"
                      type="button"
                      disabled={busy}
                      data-testid="withdraw-idea"
                      onClick={() => setConfirming(idea.id)}
                    >
                      Withdraw
                    </button>
                  </div>
                ) : null}
              </article>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Status({ idea }: { idea: Idea }): React.JSX.Element | null {
  if (idea.status === 'live') return null;

  if (idea.status === 'removed') {
    return (
      <p className="notice notice--problem" role="status" data-testid="idea-removed">
        We took this one down. It is in nobody's deck, and it is not something you did. Get in touch
        if you think that is wrong.
      </p>
    );
  }

  return (
    <p className="fine" data-testid="idea-withdrawn">
      Withdrawn. It is in nobody's deck; anybody you already matched with is still there.
    </p>
  );
}
